// OpenFOAM Case Loader

class OpenFOAMLoader {
  constructor(viewer) {
    this.viewer = viewer;
    // Use /api for subdomain setup, localhost:8082 for local development
    this.apiBase = window.location.origin.includes('localhost')
      ? 'http://localhost:8082'
      : '';
  }

  // Get case information without loading full data
  async getCaseInfo(casePath) {
    try {
      const token = window.Auth?.getToken() || localStorage.getItem('coandacloud_token');
      // Remove leading slash if present, then encode each path component separately
      const pathWithoutLeadingSlash = casePath.startsWith('/') ? casePath.substring(1) : casePath;
      const encodedPath = pathWithoutLeadingSlash.split('/').map(encodeURIComponent).join('/');
      const response = await fetch(`${this.apiBase}/api/openfoam/case-info/${encodedPath}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });

      if (!response.ok) {
        // Try to get error detail from response body
        let errorDetail = response.statusText || `HTTP ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData?.detail) errorDetail = errorData.detail;
        } catch (e) { /* ignore parse errors */ }
        throw new Error(`Failed to get case info: ${errorDetail}`);
      }

      return await response.json();

    } catch (error) {
      console.error('Error getting case info:', error);
      throw error;
    }
  }

  // List OpenFOAM cases in a directory
  async listCases(directory) {
    try {
      const token = window.Auth?.getToken() || localStorage.getItem('coandacloud_token');
      const response = await fetch(`${this.apiBase}/api/openfoam/list-cases`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ directory })
      });

      if (!response.ok) {
        throw new Error(`Failed to list cases: ${response.statusText}`);
      }

      return await response.json();

    } catch (error) {
      console.error('Error listing cases:', error);
      throw error;
    }
  }

  // Browse directory (for file browser UI)
  async browseDirectory(path) {
    try {
      const token = window.Auth?.getToken() || localStorage.getItem('coandacloud_token');
      const response = await fetch(`${this.apiBase}/api/browse`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ path })
      });

      if (!response.ok) {
        throw new Error(`Failed to browse directory: ${response.statusText}`);
      }

      return await response.json();

    } catch (error) {
      console.error('Error browsing directory:', error);
      throw error;
    }
  }

  // Delete uploaded file or folder
  async deleteUpload(relativePath) {
    try {
      const token = window.Auth?.getToken() || localStorage.getItem('coandacloud_token');
      const response = await fetch(`${this.apiBase}/api/delete-upload`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ path: relativePath })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to delete: ${response.statusText}`);
      }

      return await response.json();

    } catch (error) {
      console.error('Error deleting upload:', error);
      throw error;
    }
  }

  async downloadFolder(relativePath) {
    try {
      const token = window.Auth?.getToken() || localStorage.getItem('coandacloud_token');
      const response = await fetch(`${this.apiBase}/api/download-folder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ path: relativePath })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to download: ${response.statusText}`);
      }

      const blob = await response.blob();
      let filename = 'download.zip';
      const disposition = response.headers.get('content-disposition');
      if (disposition) {
        const match = disposition.match(/filename=\"?([^\";]+)\"?/i);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { status: 'success' };
    } catch (error) {
      console.error('Error downloading folder:', error);
      throw error;
    }
  }

  async uploadSingleFile(file) {
    const formData = new FormData();
    formData.append('file', file);

    const token = window.Auth?.getToken() || localStorage.getItem('coandacloud_token');
    const response = await fetch(`${this.apiBase}/api/upload-single-file`, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : ''
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMsg = errorData.detail || 'Upload failed';
      const error = new Error(errorMsg);

      // Check if this is a storage quota error
      if (response.status === 413 || (errorData?.detail && errorData.detail.includes('quota exceeded'))) {
        error.isQuotaExceeded = true;
      }

      throw error;
    }

    const result = await response.json();
    return result.data;
  }
}

// Export
window.OpenFOAMLoader = OpenFOAMLoader;
